/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.14.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { ETextBlockStatus } from '@components/common/TextBlockStatus/textBlockStatusTypes';
import { HttpStatus } from '@modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardChangeLock,
} from '@modules/internationalization/intSelectors';
import { IWizardChangeLockTranslation } from '@modules/internationalization/types/wizards/changeLock';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '@modules/wizard/components/WizardReport/wizardReportSagas';
import { makeSelectWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '@modules/wizard/wizardTypes';

import { ICommonTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectIsFeatureWithVersionEnabled,
} from '../PageAuth/selectors/authSelectors';
import { wizardChangeLockSetParamsAction, wizardChangeLockSetStepValidAction } from './wizardChangeLockActions';
import {
    EWizardChangeLockOperationType,
    EWizardChangeLockSteps,
    initWizardChangeLockState,
    WIZARD_CHANGE_LOCK_HANDLE_NEXT,
    WIZARD_CHANGE_LOCK_SET_PARAMS,
    WIZARD_CHANGE_LOCK_START_VALIDATE,
} from './wizardChangeLockConstant';
import { IWizardChangeLockLockDateParameters, IWizardChangeLockOrderResult } from './wizardChangeLockReducer';
import {
    makeSelectWizardChangeLockDateFrom,
    makeSelectWizardChangeLockDateTo,
    makeSelectWizardChangeLockIsOnlineCheckProceeded,
    makeSelectWizardChangeLockIsUnlockSubscribers,
    makeSelectWizardChangeLockLockParameters,
    makeSelectWizardChangeLockMaxLockingPeriod,
    makeSelectWizardChangeLockOrderCheckResult,
    makeSelectWizardChangeLockRemovedSubscribers,
    makeSelectWizardChangeLockSubscriberIds,
} from './wizardChangeLockSelectors';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */
export function* wizardChangeLockMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    yield call(wizardChangeLockLoadDataFromStorageSaga);

    switch (stepName) {
        case EWizardChangeLockSteps.StepOperationSelection:
            break;
        case EWizardChangeLockSteps.StepDateSelection:
            const isUnlockSubscribersCurrent: boolean = yield select(makeSelectWizardChangeLockIsUnlockSubscribers());
            const maxLockingPeriodCurrent: number = yield select(makeSelectWizardChangeLockMaxLockingPeriod());
            const lockParametersCurrent: IWizardChangeLockLockDateParameters = yield select(
                makeSelectWizardChangeLockLockParameters(),
            );
            const dateFromCurrent: string = yield select(makeSelectWizardChangeLockDateFrom());
            const dateToCurrent: string = yield select(makeSelectWizardChangeLockDateTo());

            const operationType: EWizardChangeLockOperationType = action.payload.step.operationType;
            const maxLockingPeriod: number = action.payload.step.maxLockingPeriod;
            const isUnlockSubscribers = operationType
                ? operationType === EWizardChangeLockOperationType.UNLOCK
                : isUnlockSubscribersCurrent;
            const lockParameters: IWizardChangeLockLockDateParameters = isUnlockSubscribers
                ? action.payload.step.unlockParameters
                : action.payload.step.lockParameters;

            yield put(
                wizardChangeLockSetParamsAction({
                    maxLockingPeriod: maxLockingPeriod || maxLockingPeriodCurrent,
                    lockParameters: lockParameters || lockParametersCurrent,
                    isUnlockSubscribers: isUnlockSubscribers,
                    dateFrom: dateFromCurrent || lockParameters?.dateFrom,
                    dateTo: dateToCurrent || lockParameters?.dateTo,
                }),
            );

            break;
        case EWizardChangeLockSteps.StepConfirmation:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */
export function* wizardChangeLockHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const dateFrom: string = yield select(makeSelectWizardChangeLockDateFrom());
    const dateTo: string = yield select(makeSelectWizardChangeLockDateTo());
    const isUnlockSubscribers: boolean = yield select(makeSelectWizardChangeLockIsUnlockSubscribers());
    const subscribersIds: number[] = yield select(makeSelectWizardChangeLockSubscriberIds());

    const params: { [key: string]: string | number[] | undefined } = {
        from: dateFrom,
        operationType: isUnlockSubscribers
            ? EWizardChangeLockOperationType.UNLOCK
            : EWizardChangeLockOperationType.LOCK,
        subsIds: subscribersIds,
        to: isUnlockSubscribers ? undefined : dateTo,
    };

    switch (stepName) {
        case EWizardChangeLockSteps.StepOperationSelection: {
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_BLOCK_MASTER_TO_STEP_2,
                    type: ELogBusinessEventType.common,
                }),
            );
            yield put(
                wizardNextAction({
                    data: params,
                }),
            );
            break;
        }
        case EWizardChangeLockSteps.StepDateSelection:
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_BLOCK_MASTER_TO_STEP_3,
                    type: ELogBusinessEventType.common,
                }),
            );
            yield put(
                wizardNextAction({
                    data: params,
                }),
            );
            break;
        case EWizardChangeLockSteps.StepConfirmation:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_BLOCK_MASTER_CREATE_ORDER,
                    type: ELogBusinessEventType.common,
                }),
            );
            yield put(
                wizardNextAction({
                    data: params,
                    ...reportParams,
                }),
            );
            break;
    }

    yield call(wizardChangeLockUpdateStorageSaga);
}

export function* wizardChangeLockHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const isUnlockSubscribers = yield select(makeSelectWizardChangeLockIsUnlockSubscribers());

    switch (stepName) {
        case EWizardChangeLockSteps.StepOperationSelection:
            const maxLockingPeriod: number = action.payload.currentStep?.step.maxLockingPeriod;
            const lockParameters: IWizardChangeLockLockDateParameters = isUnlockSubscribers
                ? action.payload.currentStep?.step.unlockParameters
                : action.payload.currentStep?.step.lockParameters;

            yield put(
                wizardChangeLockSetParamsAction({
                    dateFrom: lockParameters.dateFrom,
                    dateTo: lockParameters.dateTo,
                    lockParameters: lockParameters,
                    maxLockingPeriod: maxLockingPeriod,
                    removedSubscribers: [],
                }),
            );

            break;
        case EWizardChangeLockSteps.StepDateSelection:
            const isOnlineCheckProceeded: boolean = action.payload.currentStep?.step.isOnlineCheckProceeded;
            const orderCheckResult: IWizardChangeLockOrderResult = action.payload.currentStep?.step.orderCheckResult;
            yield put(
                wizardChangeLockSetParamsAction({
                    isOnlineCheckProceeded: isOnlineCheckProceeded,
                    orderCheckResult: orderCheckResult,
                }),
            );
            break;
        case EWizardChangeLockSteps.StepConfirmation:
            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';
            yield put(
                logBusinessEvent({
                    type: ELogBusinessEventType.wizard,
                    eventName: ELogBusinessEventName.B2B_BLOCK_MASTER_CREATE_ORDER,
                    orderId,
                }),
            );

            const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
                makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
            );

            if (isMegaPowersFeatureGreaterOrEqualThan2) {
                const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

                const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
                const goBack = () => breadCrumbGoBack(breadCrumbsData);

                yield put(
                    modalOpen({
                        render: () =>
                            createElement(ModalMultipleOrders, {
                                isOpen: true,
                                orderOrOrders: orderId,
                                onClose: goBack,
                                onSuccessButtonClick: breadCrumbGoBack,
                                orderTypeTranlation: orderCreationModal.success.orderType.LOCK,
                            }),
                    }),
                );
            } else {
                yield put(wizardOpenModalSummaryAction({ orderId }));
            }
            break;
    }

    yield call(wizardChangeLockLoadDataFromStorageSaga);
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardChangeLockUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const isUnlockSubscribers = yield select(makeSelectWizardChangeLockIsUnlockSubscribers());
    const dateFrom = yield select(makeSelectWizardChangeLockDateFrom());
    const dateTo = yield select(makeSelectWizardChangeLockDateTo());
    const lockParameters = yield select(makeSelectWizardChangeLockLockParameters());
    const maxLockingPeriod = yield select(makeSelectWizardChangeLockMaxLockingPeriod());
    const orderCheckResult = yield select(makeSelectWizardChangeLockOrderCheckResult());
    const isOnlineCheckProceeded = yield select(makeSelectWizardChangeLockIsOnlineCheckProceeded());

    switch (stepName) {
        case EWizardChangeLockSteps.StepOperationSelection:
            yield put(
                wizardStorageUpdateStepAction({
                    isUnlockSubscribers: isUnlockSubscribers,
                    removedSubscribers: [],
                }),
            );
            break;
        case EWizardChangeLockSteps.StepDateSelection:
            yield put(
                wizardStorageUpdateStepAction({
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                    isUnlockSubscribers: isUnlockSubscribers,
                    lockParameters: lockParameters,
                    maxLockingPeriod: maxLockingPeriod,
                    orderCheckResult,
                    isOnlineCheckProceeded,
                }),
            );
            break;
        case EWizardChangeLockSteps.StepConfirmation:
            const removedSubscribers = yield select(makeSelectWizardChangeLockRemovedSubscribers());
            yield put(
                wizardStorageUpdateStepAction({
                    removedSubscribers,
                }),
            );
            break;
    }
}

export function* wizardChangeLockLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     */
    if (localStorageParams) {
        yield put(
            wizardChangeLockSetParamsAction({
                isUnlockSubscribers:
                    localStorageParams.isUnlockSubscribers || initWizardChangeLockState.isUnlockSubscribers,
            }),
        );
        if (localStorageParams.lockParameters) {
            const dateFrom = yield select(makeSelectWizardChangeLockDateFrom());
            const dateTo = yield select(makeSelectWizardChangeLockDateTo());
            const lockParameters = yield select(makeSelectWizardChangeLockLockParameters());
            const orderCheckResult = yield select(makeSelectWizardChangeLockOrderCheckResult());
            const isOnlineCheckProceeded = yield select(makeSelectWizardChangeLockIsOnlineCheckProceeded());
            const removedSubscribers = yield select(makeSelectWizardChangeLockRemovedSubscribers());
            yield put(
                wizardChangeLockSetParamsAction({
                    dateFrom: dateFrom ? dateFrom : localStorageParams.dateFrom,
                    dateTo: dateTo ? dateTo : localStorageParams.dateTo,
                    lockParameters: lockParameters ? lockParameters : localStorageParams.lockParameters,
                    maxLockingPeriod: localStorageParams.maxLockingPeriod,
                    orderCheckResult: orderCheckResult?.orderSubs.length
                        ? orderCheckResult
                        : localStorageParams.orderCheckResult,
                    isOnlineCheckProceeded: isOnlineCheckProceeded || localStorageParams.isOnlineCheckProceeded,
                    removedSubscribers: removedSubscribers?.length
                        ? removedSubscribers
                        : localStorageParams.removedSubscribers || [],
                }),
            );
        }
    }

    yield call(wizardChangeLockUpdateStorageSaga);
}

export function* wizardChangeLockStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

export function* wizardChangeLockValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());

    const isUnlockSubscribers: boolean = yield select(makeSelectWizardChangeLockIsUnlockSubscribers());
    const orderCheckResult: IWizardChangeLockOrderResult = yield select(makeSelectWizardChangeLockOrderCheckResult());
    const subscribersIds: number[] = yield select(makeSelectWizardChangeLockSubscriberIds());
    const translation: IWizardChangeLockTranslation = yield select(makeSelectTranslationWizardChangeLock());

    switch (stepName) {
        case EWizardChangeLockSteps.StepOperationSelection:
        case EWizardChangeLockSteps.StepDateSelection:
            break;
        case EWizardChangeLockSteps.StepConfirmation:
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }

            const subscribersWithoutErrors = orderCheckResult.orderSubs.filter(
                (i) => subscribersIds.includes(i.subsId) && ETextBlockStatus.ERROR !== i.status,
            );

            const isReportValid = yield call(wizardReportValidateSaga);
            isStepValid = subscribersIds.length > 0 && subscribersWithoutErrors.length > 0 && isReportValid;
            if (subscribersIds.length < 1) {
                validateError = translation.validateErrors.selectOneSubscribers;
                break;
            }
            if (subscribersWithoutErrors.length < 1) {
                validateError = isUnlockSubscribers
                    ? translation.validateErrors.unlockSubscribersWithErrors
                    : translation.validateErrors.lockSubscribersWithErrors;
                break;
            }
            validateError = isStepValid ? '' : translation.validateErrors.fillNotify;
            break;
    }
    yield put(
        wizardChangeLockSetStepValidAction({
            isStepValid,
            validateError,
        }),
    );
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */
export function* wizardChangeLockFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationChangeLock = yield select(makeSelectTranslationWizardChangeLock());
        title = translationChangeLock.error.codes[errorCode] || translationCommon.error.codes[errorCode];
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */
export function* wizardChangeLockSagas(): SagaIterator {
    yield all([
        // initialization
        takeEvery(WIZARD_START_SUCCESS, wizardChangeLockStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardChangeLockMetaSaga),
        // next
        takeEvery(WIZARD_CHANGE_LOCK_HANDLE_NEXT, wizardChangeLockHandleNextSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardChangeLockHandleNextSuccessSaga),
        takeEvery(WIZARD_BACK_SUCCESS, wizardChangeLockLoadDataFromStorageSaga),
        // validate
        takeEvery([WIZARD_CHANGE_LOCK_START_VALIDATE, WIZARD_CHANGE_LOCK_SET_PARAMS], wizardChangeLockValidateSaga),
        takeEvery(WIZARD_CHANGE_LOCK_SET_PARAMS, wizardChangeLockUpdateStorageSaga),
        // failed
        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardChangeLockFailedSaga,
        ),
    ]);
}
